import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import tarotCards from '../data/tarotCards';
import { useLanguage } from '../context/LanguageContext';
import sound from '../audio/soundManager';

// ---------- Animations ----------
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const cardReveal = keyframes`
  0%   { opacity: 0; transform: scale(0.85) translateY(20px); }
  60%  { opacity: 1; transform: scale(1.04) translateY(-4px); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
`;

const haloPulse = keyframes`
  0%, 100% { box-shadow: 0 0 12px rgba(156, 107, 255, 0.25); }
  50%      { box-shadow: 0 0 26px rgba(212, 175, 55, 0.45); }
`;

const spinSlow = keyframes`
  to { transform: rotate(360deg); }
`;

// ---------- Layout ----------
const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: var(--space-5) var(--space-4);
  text-align: center;
  animation: ${fadeUp} 0.6s var(--ease-soft);
`;

const Title = styled.h1`
  font-family: var(--font-display);
  color: var(--color-gold-soft);
  font-size: clamp(1.8rem, 3.5vw, 2.4rem);
  margin-bottom: var(--space-3);
  text-shadow: 0 0 22px rgba(156, 107, 255, 0.35);
`;

const Subtitle = styled.p`
  margin: 0 auto var(--space-4);
  font-size: var(--fs-md);
  color: var(--color-text-secondary);
  max-width: 560px;
  line-height: var(--lh-normal);
`;

const SelectionText = styled.p`
  margin: var(--space-4) 0 var(--space-5);
  font-family: var(--font-display);
  letter-spacing: 0.18em;
  text-transform: uppercase;
  font-size: 0.95rem;
  color: var(--color-gold);
`;

const SpreadContainer = styled.div`
  position: relative;
  height: 620px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--space-4) 0;

  @media (max-width: 768px) {
    height: 540px;
  }
`;

const SemiCircleRow = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  margin-bottom: var(--space-4);
`;

const Card = styled.div`
  width: 84px;
  height: 128px;
  border-radius: 10px;
  background-image:
    linear-gradient(135deg, rgba(156, 107, 255, 0.10), rgba(11, 16, 32, 0.0)),
    url(${process.env.PUBLIC_URL}/images/tarot/back.jpg);
  background-size: cover;
  background-position: center;
  box-shadow:
    0 6px 16px rgba(0, 0, 0, 0.55),
    inset 0 0 0 1px rgba(212, 175, 55, 0.25);
  cursor: pointer;
  transition: transform var(--dur-base) var(--ease-soft),
              box-shadow var(--dur-base) var(--ease-soft),
              filter var(--dur-base) var(--ease-soft);
  position: absolute;
  transform: rotate(${props => props.rotation}deg) translate(-50%, -50%);
  left: ${props => props.x}%;
  top: ${props => props.y}%;
  transform-origin: center;

  &:hover {
    transform: rotate(${props => props.rotation}deg) translate(-50%, -64%);
    filter: brightness(1.15);
    box-shadow:
      0 14px 26px rgba(0, 0, 0, 0.6),
      inset 0 0 0 1px rgba(246, 231, 193, 0.6),
      0 0 22px rgba(156, 107, 255, 0.45);
  }
`;

const SelectedCardsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--space-5);
  margin-top: var(--space-6);
  margin-bottom: var(--space-4);
  animation: ${fadeUp} 0.5s var(--ease-soft);
`;

const SelectedCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--space-4) var(--space-3);
  border-radius: var(--radius-md);
  background: rgba(21, 26, 51, 0.55);
  border: 1px solid var(--color-border-soft);
  min-width: 150px;
`;

const PositionLabel = styled.div`
  font-family: var(--font-display);
  letter-spacing: 0.18em;
  text-transform: uppercase;
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: var(--space-3);
  color: var(--color-gold);
`;

const SelectedCard = styled.div`
  width: 130px;
  height: 200px;
  border-radius: 10px;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  margin-bottom: var(--space-3);
  transform: ${props => props.reversed ? 'rotate(180deg)' : 'rotate(0deg)'};
  animation: ${cardReveal} 0.6s var(--ease-soft);
  box-shadow:
    0 8px 20px rgba(0, 0, 0, 0.6),
    inset 0 0 0 1px rgba(212, 175, 55, 0.45),
    0 0 20px rgba(156, 107, 255, 0.25);
`;

const CardName = styled.div`
  font-family: var(--font-display);
  font-size: 0.85rem;
  letter-spacing: 0.06em;
  color: var(--color-text-primary);
  margin-top: var(--space-2);
  text-align: center;
`;

const InterpretButton = styled.button`
  background: linear-gradient(135deg, var(--color-accent-purple), var(--color-accent-violet));
  color: var(--color-text-primary);
  border: 1px solid rgba(246, 231, 193, 0.4);
  padding: 14px 36px;
  font-family: var(--font-display);
  letter-spacing: 0.18em;
  text-transform: uppercase;
  font-size: 0.95rem;
  font-weight: 600;
  border-radius: var(--radius-pill);
  cursor: pointer;
  margin-top: var(--space-6);
  transition: transform var(--dur-base) var(--ease-soft),
              box-shadow var(--dur-base) var(--ease-soft),
              filter var(--dur-base) var(--ease-soft);
  box-shadow: 0 8px 22px rgba(110, 76, 207, 0.5);
  animation: ${haloPulse} 4s ease-in-out infinite;

  &:hover {
    transform: translateY(-2px);
    filter: brightness(1.1);
    box-shadow: var(--glow-violet-strong);
  }

  &:focus-visible {
    outline: 2px solid var(--color-gold);
    outline-offset: 3px;
  }

  &:disabled {
    background: rgba(80, 80, 100, 0.4);
    color: var(--color-text-muted);
    cursor: not-allowed;
    box-shadow: none;
    animation: none;
  }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--color-bg-overlay);
  backdrop-filter: blur(6px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  gap: var(--space-4);
`;

const LoadingHalo = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: 2px solid rgba(212, 175, 55, 0.25);
  border-top-color: var(--color-gold);
  animation: ${spinSlow} 1.6s linear infinite;
  box-shadow: 0 0 24px rgba(212, 175, 55, 0.35);
`;

const LoadingText = styled.div`
  background: var(--color-bg-panel-strong);
  border: 1px solid var(--color-border-strong);
  padding: var(--space-4) var(--space-6);
  border-radius: var(--radius-md);
  font-family: var(--font-display);
  letter-spacing: 0.1em;
  font-size: 1rem;
  color: var(--color-gold-soft);
  box-shadow: var(--shadow-elevated);
`;

// --------------------------
// Component Logic (UNCHANGED)
// --------------------------
const CardSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, locale } = useLanguage();
  const defaultQ = t('questionForm.defaultQuestion');
  const { question } = location.state || { question: defaultQ };

  const [selectedCards, setSelectedCards] = useState([]);
  const [spreadCards, setSpreadCards] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const cards = [];

    for (let row = 0; row < 3; row++) {
      for (let i = 0; i < 26; i++) {
        const angle = (i / 25) * 180;
        const radians = (angle * Math.PI) / 180;
        const x = 50 + 45 * Math.cos(radians);
        const y = 50 + 20 * Math.sin(radians);
        const rotation = (Math.random() - 0.5) * 20;

        cards.push({
          id: row * 26 + i,
          x,
          y,
          rotation,
          row
        });
      }
    }

    setSpreadCards(cards);
  }, []);

  const handleCardSelect = (cardId) => {
    if (selectedCards.length >= 3 || selectedCards.includes(cardId)) return;

    const availableCards = tarotCards.filter(card =>
      !selectedCards.some(sc => sc.id === card.id)
    );
    const randomCard = availableCards[Math.floor(Math.random() * availableCards.length)];
    const isReversed = Math.random() > 0.5;

    const newCard = {
      ...randomCard,
      reversed: isReversed
    };

    sound.playCardSelect();
    setSelectedCards([...selectedCards, newCard]);
  };

  const handleInterpretation = async () => {
    sound.playInterpret();
    setLoading(true);

    try {
      const cardNames = selectedCards.map(card =>
        `${card.name}${card.reversed ? ' (Reversed)' : ''}`
      ).join(', ');

      const response = await fetch('/api/interpretTarot', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question,
          cards: selectedCards,
          text: cardNames,
          locale,
        }),
      });

      if (!response.ok) throw new Error('Failed to get interpretation');

      const data = await response.json();

      navigate('/interpretation', {
        state: {
          question,
          cards: selectedCards,
          interpretation: data.interpretation,
          fallback: data.fallback,
        },
      });
    } catch (error) {
      console.error('Error getting interpretation:', error);
      navigate('/interpretation', {
        state: {
          question,
          cards: selectedCards,
          error: true
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const positions = [t('positions.past'), t('positions.present'), t('positions.future')];

  return (
    <Container>
      <Title>{t('cardSelection.title')}</Title>
      <Subtitle>{t('cardSelection.subtitle')}</Subtitle>

      {selectedCards.length !== 3 && (
        <SelectionText>
          {t('cardSelection.selectMore', { count: 3 - selectedCards.length })}
        </SelectionText>
      )}

      <SpreadContainer>
        {[0, 1, 2].map(row => (
          <SemiCircleRow key={row}>
            {spreadCards.filter(card => card.row === row).map((card, index) => (
              <Card
                key={index}
                x={card.x}
                y={card.y}
                rotation={card.rotation}
                onClick={() => handleCardSelect(card.id)}
              />
            ))}
          </SemiCircleRow>
        ))}
      </SpreadContainer>

      {selectedCards.length > 0 && (
        <SelectedCardsContainer>
          {selectedCards.map((card, index) => {
            const position = positions[index];
            return (
              <SelectedCardWrapper key={index}>
                <PositionLabel>{position}</PositionLabel>
                <SelectedCard image={card.image} reversed={card.reversed} />
                <CardName>
                  {card.name} ({card.reversed ? t('orientation.reversed') : t('orientation.upright')})
                </CardName>
              </SelectedCardWrapper>
            );
          })}
        </SelectedCardsContainer>
      )}

      {selectedCards.length === 3 && (
        <InterpretButton onClick={handleInterpretation}>
          {t('cardSelection.getInterpretation')}
        </InterpretButton>
      )}

      {loading && (
        <LoadingOverlay>
          <LoadingHalo />
          <LoadingText>{t('cardSelection.loading')}</LoadingText>
        </LoadingOverlay>
      )}
    </Container>
  );
};

export default CardSelection;
